import React, { useState } from 'react';
import { Card, CardBody } from './Card';
import { SectionHeader } from './SectionHeader';
import { Link } from 'react-router-dom';
import { Button } from './Button';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useConfirm } from './ConfirmationModal';
import { useTenantUserNames } from '../../services/tenantUserNames';
import { resolveNoteAuthor } from '../../utils/displayName';

/**
 * NotesPanel — canonical notes display + add-note form.
 * REQ-COMP-07 (Iteration 16 Component Unification Contract)
 *
 * Props:
 *   notes — array of { id, content, author, author_id?, created_at, sourceLabel?, sourceHref? }
 *           IDI-550: the author shown is the author's CURRENT profile name,
 *           looked up by author_id in the tenant's user list (once per
 *           session), else the stored author; a stored "You" shows "Unknown".
 *           IDI-522: an optional sourceLabel (e.g. the work order a note came
 *           from) renders beside the author; sourceHref makes it a link.
 *   onAdd — callback(content: string) when user submits a note
 *   loading — show loading state
 *   IDI-552 (optional; callers that pass none see no change):
 *   onEdit — async callback(note, content: string); the inline editor
 *            closes when it resolves and stays open (text kept) if it throws
 *   onDelete — async callback(note), called only after the user confirms
 *   currentUserId — the signed-in user's Cognito sub. The pencil + trash show
 *            on a note only when its author_id equals this (author only,
 *            admins included; a note with no author_id shows neither)
 *   canModify — optional predicate(note) narrowing which notes may be
 *            modified at all (e.g. the job tab: quote-sourced rows only)
 *   The time shown is updated_at || created_at; there is no "edited" label.
 */
export function NotesPanel({
  notes = [], onAdd, loading, className,
  onEdit, onDelete, currentUserId, canModify,
}) {
  const [newNote, setNewNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const namesBySub = useTenantUserNames();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = newNote.trim();
    if (!trimmed || !onAdd) return;
    setSubmitting(true);
    try {
      await onAdd(trimmed);
      setNewNote('');
    } catch (err) {
      // IDI-305: a failed add keeps the typed text so nothing is lost; the
      // caller reports the failure (toast). Not rethrown: a form submit
      // handler has no one to catch it.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card data-portica-role="notes-panel" className={className}>
      <CardBody>
        <SectionHeader title="Notes" count={notes.length} />

        {/* Note list */}
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {loading && notes.length === 0 && (
            <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
              Loading notes…
            </p>
          )}
          {!loading && notes.length === 0 && (
            <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
              No notes yet.
            </p>
          )}
          {notes.map((note) => (
            <NoteItem
              key={note.id}
              note={note}
              namesBySub={namesBySub}
              onEdit={onEdit}
              onDelete={onDelete}
              mine={isOwnNote(note, currentUserId, canModify)}
            />
          ))}
        </div>

        {/* Add note form */}
        {onAdd && (
          <form onSubmit={handleSubmit} className="mt-4">
            <textarea
              className="input resize-none w-full"
              rows={2}
              placeholder="Add a note…"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              disabled={submitting}
            />
            <div className="flex justify-end mt-2">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={submitting}
                loading={submitting}
              >
                Add Note
              </Button>
            </div>
          </form>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * IDI-552: may the signed-in user edit/delete this note? Only its author: the
 * note's author_id must be present and equal the caller's sub. The server
 * enforces the same rule (403) — this only hides the buttons.
 */
export function isOwnNote(note, currentUserId, canModify) {
  if (!note || !currentUserId) return false;
  const aid = note.author_id || note.authorId;
  if (!aid || aid !== currentUserId) return false;
  return typeof canModify === 'function' ? !!canModify(note) : true;
}

function NoteItem({ note, namesBySub, onEdit, onDelete, mine }) {
  const confirm = useConfirmSafe();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const canEdit = mine && typeof onEdit === 'function';
  const canDelete = mine && typeof onDelete === 'function';
  const shownAt = note.updated_at || note.created_at;

  const startEdit = () => {
    setDraft(note.content || '');
    setEditing(true);
  };
  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      await onEdit(note, trimmed);
      setEditing(false);
    } catch (err) {
      // Keep the editor open with the typed text; the caller toasts.
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    const ok = await confirm({
      title: "Delete this note? This can't be undone.",
      confirmLabel: 'Delete note',
      cancelLabel: 'Keep',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await onDelete(note);
    } catch (err) {
      // The caller toasts; the note stays.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-md px-3 py-2"
      data-testid="note-item"
      style={{ backgroundColor: 'var(--color-surface-sunken)' }}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs min-w-0 truncate">
          <span className="font-medium" data-testid="note-author" style={{ color: 'var(--color-text-default)' }}>
            {resolveNoteAuthor(note, namesBySub)}
          </span>
          {note.sourceLabel && (
            <>
              <span aria-hidden="true" style={{ color: 'var(--color-text-tertiary)' }}> · </span>
              {note.sourceHref ? (
                <Link
                  to={note.sourceHref}
                  state={note.sourceState}
                  data-testid="note-source-link"
                  className="hover:underline"
                  style={{ color: 'var(--color-text-link, var(--color-primary))' }}
                >
                  {note.sourceLabel}
                </Link>
              ) : (
                <span data-testid="note-source-label" style={{ color: 'var(--color-text-tertiary)' }}>
                  {note.sourceLabel}
                </span>
              )}
            </>
          )}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          <span
            className="text-xs"
            data-testid="note-time"
            style={{ color: 'var(--color-text-tertiary)' }}
            title={shownAt ? new Date(shownAt).toLocaleString() : undefined}
          >
            {formatRelativeTime(shownAt)}
          </span>
          {canEdit && !editing && (
            <button
              type="button"
              className="p-1 rounded hover:bg-black/5"
              aria-label="Edit note"
              title="Edit note"
              data-testid="note-edit"
              onClick={startEdit}
              disabled={busy}
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          {canDelete && !editing && (
            <button
              type="button"
              className="p-1 rounded hover:bg-black/5"
              aria-label="Delete note"
              title="Delete note"
              data-testid="note-delete"
              onClick={remove}
              disabled={busy}
              style={{ color: 'var(--color-text-tertiary)' }}
            >
              <TrashIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </span>
      </div>
      {editing ? (
        <div>
          <textarea
            className="input resize-none w-full"
            rows={3}
            aria-label="Edit note"
            data-testid="note-edit-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={busy}
            autoFocus
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={save}
              disabled={busy || !draft.trim()}
              loading={busy}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--color-text-secondary)' }}>
          {note.content}
        </p>
      )}
    </div>
  );
}

// useConfirm throws outside a <ConfirmProvider>. Existing callers (and their
// tests) render NotesPanel without one and never delete, so only resolve the
// hook's absence lazily: without a provider, Delete is simply never confirmed.
function useConfirmSafe() {
  try {
    return useConfirm();
  } catch (e) {
    return async () => false;
  }
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export default NotesPanel;
